import { FaroTable } from "@components/common/faro-table/faro-table";
import { MemberGroupProjectsBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { getMemberGroupProjectsColumns } from "@components/table/member-group-projects/member-group-projects-columns";
import { MemberGroupProjectsHeaders } from "@components/table/member-group-projects/member-group-projects-table-utils";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-pro";
import { useCompanyId } from "@context-providers/company-id-provider";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { ProjectEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useCallback } from "react";

interface Props {
  /** The projects that is shown in the table */
  projects: FaroCoreApiTypes.ProjectSummary[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: MemberGroupProjectsHeaders[];

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: MemberGroupProjectsBulkButtonComponents;

  /** Whether to hide the row selection checkboxes */
  shouldHideCheckbox?: boolean;
}

/**
 * Listing all the projects in a group that a member belongs to
 */
export function MemberGroupProjectsTable({
  projects,
  isLoading,
  requiredColumns,
  bulkActionButtons,
  shouldHideCheckbox = true,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();
  const { navigateToProjectDetail } = useAppNavigation();
  const companyId = useCompanyId();
  const { trackEvent } = useTrackEvent();

  /**
   * Prevent selecting resource-owner rows because the backend does not allow
   * removing a project's resource owner via this bulk action.
   */
  const isRowSelectable = useCallback(
    (params: GridRowParams) => params.row.entity?.role !== "RESOURCE_OWNER",
    []
  );

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.memberGroupProjects));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  return (
    <Box component="div" sx={{ pb: "24px" }}>
      <FaroTable
        entities={projects}
        uniqueIdKey={"id"}
        isLoading={isLoading}
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        columns={requiredColumns.map(
          (requiredColumn) =>
            getMemberGroupProjectsColumns({
              options: {
                isScreenLgOrLarger,
                isScreenMd,
              },
            })[requiredColumn]
        )}
        bulkActionButtons={bulkActionButtons}
        rowHeight={70}
        shouldHideCheckbox={shouldHideCheckbox}
        isRowSelectable={isRowSelectable}
        shouldHideHeader={true}
        shouldSkipDynamicHeight={true}
        tableType={TableType.memberGroupProjects}
        onRowClick={(project) => {
          trackEvent({
            name: ProjectEvents.openProjectDetails,
            props: {
              projectId: project.id,
              eventLocation: "user-profile-group-projects",
            },
          });
          navigateToProjectDetail({
            companyId,
            projectId: project.id,
          });
        }}
      />
    </Box>
  );
}
